import axios from 'axios';
import { getAuthToken } from '../utils/authUtils';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || '';

/**
 * Facility CPT Pricing Service
 * Manages facility-specific procedure code pricing
 */

/**
 * Get CPT prices for a specific facility
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Array>} Array of price objects
 */
export async function getFacilityCPTPrices(facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/admin/facility/${facilityId}/cpt-prices`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data.prices || [];
    }

    throw new Error('Failed to get facility CPT prices');
  } catch (error) {
    console.error('Error getting facility CPT prices:', error);
    throw error;
  }
}

/**
 * Save/update CPT prices for a specific facility
 * @param {string} facilityId - The facility ID
 * @param {Array} prices - Array of price objects { procedureCodeId, procedureCode, procedureName, price }
 * @returns {Promise<Object>} Result of the save operation
 */
export async function saveFacilityCPTPrices(facilityId, prices) {
  try {
    const token = await getAuthToken();
    const response = await axios.put(
      `${API_BASE}/admin/facility/${facilityId}/cpt-prices`,
      { prices },
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to save facility CPT prices');
  } catch (error) {
    console.error('Error saving facility CPT prices:', error);
    throw error;
  }
}

/**
 * Copy prices from one facility to another
 * @param {string} sourceFacilityId - The source facility to copy from
 * @param {string} targetFacilityId - The target facility to copy to
 * @returns {Promise<Object>} Result of the copy operation
 */
export async function copyPricesFromFacility(sourceFacilityId, targetFacilityId) {
  try {
    // Get prices from source facility
    const sourcePrices = await getFacilityCPTPrices(sourceFacilityId);
    
    if (!sourcePrices || sourcePrices.length === 0) {
      return { success: true, message: 'No prices to copy', count: 0 };
    }

    // Map to format for saving (remove source-specific IDs)
    const pricesToCopy = sourcePrices.map(price => ({
      procedureCodeId: price.procedureCodeId,
      procedureCode: price.procedureCode,
      procedureName: price.procedureName,
      price: price.price
    }));

    // Save to target facility
    const result = await saveFacilityCPTPrices(targetFacilityId, pricesToCopy);
    
    return {
      success: true,
      message: `Copied ${pricesToCopy.length} prices from source facility`,
      count: pricesToCopy.length,
      ...result
    };
  } catch (error) {
    console.error('Error copying prices from facility:', error);
    throw error;
  }
}

/**
 * Get encounter types for a facility (for copy feature)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Object with encounterTypes array
 */
export async function getFacilityEncounterTypes(facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/admin/facility/${facilityId}/encounter-types`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to get facility encounter types');
  } catch (error) {
    console.error('Error getting facility encounter types:', error);
    throw error;
  }
}

/**
 * Get departments for a facility (for copy feature)
 * @param {string} facilityId - The facility ID
 * @returns {Promise<Object>} Object with departments array
 */
export async function getFacilityDepartments(facilityId) {
  try {
    const token = await getAuthToken();
    const response = await axios.get(`${API_BASE}/admin/facility/${facilityId}/departments`, {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (response.status === 200) {
      return response.data;
    }

    throw new Error('Failed to get facility departments');
  } catch (error) {
    console.error('Error getting facility departments:', error);
    throw error;
  }
}

const facilityPricingService = {
  getFacilityCPTPrices,
  saveFacilityCPTPrices,
  copyPricesFromFacility,
  getFacilityEncounterTypes,
  getFacilityDepartments
};

export default facilityPricingService;

